import type { FieldConfig } from '../../components/generic/fieldRenderers';
import type { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate, formatDateTime } from '../../config/appConfig';
import { getEFormReferenceSource } from './referenceSources';
import { type EFormSidebarPlacementsSetting } from './sidebarPlacements';

export const EFORM_FIELD_TYPES = [
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'date',
  'time',
  'select',
  'radio',
  'checkbox',
  'switch',
  'checklist',
  'richtext',
  'signature',
  'heading',
  'spacer',
  'file',
  'photo',
  'subform',
] as const;

export type EFormFieldType = (typeof EFORM_FIELD_TYPES)[number];

/** Nested field types allowed inside a sub-form (one level; no nested subform/file/etc.). */
export const EFORM_SUBFORM_NESTED_TYPES = [
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'date',
  'time',
  'select',
  'radio',
  'checkbox',
  'switch',
] as const;

export type EFormSubformNestedType = (typeof EFORM_SUBFORM_NESTED_TYPES)[number];

export function isEFormSubformNestedType(value: unknown): value is EFormSubformNestedType {
  return (
    typeof value === 'string' &&
    (EFORM_SUBFORM_NESTED_TYPES as readonly string[]).includes(value)
  );
}

export type EFormFieldOption = {
  value: string;
  label: string;
};

export type EFormOptionsSource = 'static' | 'reference';

export type EFormVisibilityOperator = 'eq' | 'neq' | 'empty' | 'notEmpty' | 'contains';

export type EFormFieldVisibleWhen = {
  fieldKey: string;
  operator: EFormVisibilityOperator;
  value?: string;
};

export type EFormFieldValidation = {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  message?: string;
};

export type EFormFileMeta = {
  storageKey: string;
  fileName: string;
  mimeType: string;
  size: number;
};

export type EFormFieldSchema = {
  id: string;
  key: string;
  type: EFormFieldType;
  label: string;
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  options?: EFormFieldOption[];
  optionsSource?: EFormOptionsSource;
  referenceKey?: string;
  /** Width in a 12-unit row (1–12). Legacy 6 = half, 12 = full. */
  colSpan?: EFormColSpan;
  defaultValue?: string | number | boolean | string[] | null;
  validation?: EFormFieldValidation;
  visibleWhen?: EFormFieldVisibleWhen;
  readOnly?: boolean;
  /** Per effective form role: hide, show read-only, or allow editing. */
  roleAccess?: Partial<Record<EFormRole, 'hidden' | 'read' | 'write'>>;
  /**
   * Printed layout (heading, static text, memo, formatted text).
   * Not filled in; rendered as document content.
   */
  display?: boolean;
  /** When set, value is copied from another field (display/calc helper). */
  calculatedFrom?: string;
  /** File fields: allow more than one file. */
  multiple?: boolean;
  /** File / photo fields: HTML accept attribute (e.g. ".pdf,image/*"). */
  accept?: string;
  /**
   * Signature fields: also capture an official stamp/seal image
   * alongside the drawn or uploaded signature.
   */
  signatureWithStamp?: boolean;
  /** Sub-form: nested column fields (one level only). */
  fields?: EFormFieldSchema[];
  /** Sub-form: minimum number of rows. */
  minRows?: number;
  /** Sub-form: maximum number of rows. */
  maxRows?: number;
  /**
   * Sub-form row UI: `modal` opens a form dialog per row;
   * `inline` edits cells in the table.
   */
  rowEditor?: 'modal' | 'inline';
};

export const EFORM_SUBFORM_ROW_EDITORS = ['modal', 'inline'] as const;
export type EFormSubformRowEditor = (typeof EFORM_SUBFORM_ROW_EDITORS)[number];

export const EFORM_SECTION_COLUMNS = [1, 2, 3, 4, 5, 6] as const;
export type EFormSectionColumns = (typeof EFORM_SECTION_COLUMNS)[number];

export const EFORM_COL_SPANS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
export type EFormColSpan = (typeof EFORM_COL_SPANS)[number];

export function normalizeFieldColSpan(value: unknown, fallback: EFormColSpan = 6): EFormColSpan {
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isInteger(n) && n >= 1 && n <= 12) return n as EFormColSpan;
  return fallback;
}

/** Default field width when dropping into a section (12 / columns). */
export function defaultColSpanForSection(columns: EFormSectionColumns): EFormColSpan {
  switch (columns) {
    case 1:
      return 12;
    case 2:
      return 6;
    case 3:
      return 4;
    case 4:
      return 3;
    case 5:
      return 2;
    case 6:
      return 2;
  }
}

export function fieldUsesFullWidthDefault(type: EFormFieldType, display?: boolean): boolean {
  return (
    type === 'heading' ||
    type === 'textarea' ||
    type === 'richtext' ||
    type === 'signature' ||
    type === 'file' ||
    type === 'subform' ||
    Boolean(display && type !== 'spacer')
  );
}

export function resolveFieldColSpan(field: EFormFieldSchema): EFormColSpan {
  if (field.colSpan != null) return normalizeFieldColSpan(field.colSpan);
  return fieldUsesFullWidthDefault(field.type, field.display) ? 12 : 6;
}

/** Tailwind span for a 12-unit section grid. Classes are literal for JIT. */
export function fieldColSpanClass(span: EFormColSpan): string {
  switch (span) {
    case 1:
      return 'md:col-span-1';
    case 2:
      return 'md:col-span-2';
    case 3:
      return 'md:col-span-3';
    case 4:
      return 'md:col-span-4';
    case 5:
      return 'md:col-span-5';
    case 6:
      return 'md:col-span-6';
    case 7:
      return 'md:col-span-7';
    case 8:
      return 'md:col-span-8';
    case 9:
      return 'md:col-span-9';
    case 10:
      return 'md:col-span-10';
    case 11:
      return 'md:col-span-11';
    case 12:
      return 'md:col-span-12';
  }
}

export function colSpanOptionLabel(span: EFormColSpan): string {
  switch (span) {
    case 2:
      return '2 · 1/6';
    case 3:
      return '3 · 1/4';
    case 4:
      return '4 · 1/3';
    case 6:
      return '6 · 1/2';
    case 8:
      return '8 · 2/3';
    case 9:
      return '9 · 3/4';
    case 12:
      return '12 · Full';
    default:
      return `${span} of 12`;
  }
}

export type EFormSectionSchema = {
  id: string;
  heading: string;
  subHeading?: string;
  columns?: EFormSectionColumns;
  fields: EFormFieldSchema[];
  visibleWhen?: EFormFieldVisibleWhen;
};

/** Who may fill / view submissions for this form (empty = anyone with module permission). */
export type EFormRole = 'owner' | 'editor' | 'filler' | 'viewer';

export type EFormRoleAssignment = {
  role: EFormRole;
  userIds?: number[];
  departmentIds?: number[];
};

export type EFormAccessSettings = {
  assignments?: EFormRoleAssignment[];
  defaultRole?: 'filler' | 'viewer' | 'none';
  fillerUserIds?: number[];
  viewerUserIds?: number[];
};

export type EFormWorkflowCondition = {
  fieldKey: string;
  operator: 'eq' | 'neq' | 'in' | 'contains' | 'empty' | 'notEmpty' | 'gte' | 'lte';
  value?: unknown;
};

export type EFormWorkflowRule = {
  id: string;
  label: string;
  templateCode: string;
  priority?: number;
  conditions: EFormWorkflowCondition[];
};

export type EFormWorkflowAction = {
  id: string;
  event: 'approved' | 'rejected' | 'returned' | 'cancelled';
  type: 'set_field' | 'notification' | 'webhook';
  fieldKey?: string;
  value?: unknown;
  url?: string;
  recipients?: string[];
};

/** Submissions list column: meta keys or a form field key. */
export type EFormListColumnConfig = {
  key: string;
  visible: boolean;
};

export type EFormSchemaSettings = {
  requireApproval?: boolean;
  /**
   * Approval template code for type E_FORM_SUBMISSION.
   * Empty/undefined → default matching template.
   */
  approvalTemplateCode?: string;
  workflowRules?: EFormWorkflowRule[];
  workflowActions?: EFormWorkflowAction[];
  /** Legacy schema access; new definitions persist accessConfig on the definition. */
  access?: EFormAccessSettings;
  /** Ordered submissions-list columns (visibility + order). */
  listColumns?: EFormListColumnConfig[];
  /** Select/radio field keys shown as filters on the submissions list (order preserved). */
  filterFields?: string[];
  /** Where shortcut links for this form should appear in sidebar. */
  sidebarPlacements?: EFormSidebarPlacementsSetting;
};

export function applyEFormRoleAccess(
  schema: EFormSchema,
  role: EFormRole | null | undefined,
): EFormSchema {
  if (!role) return schema;
  const applyField = (field: EFormFieldSchema): EFormFieldSchema | null => {
    const access = field.roleAccess?.[role] ?? (role === 'viewer' ? 'read' : 'write');
    if (access === 'hidden') return null;
    const fields = field.fields
      ?.map(applyField)
      .filter((nested): nested is EFormFieldSchema => Boolean(nested));
    return {
      ...field,
      ...(field.fields ? { fields } : {}),
      readOnly: field.readOnly || access === 'read',
    };
  };
  return {
    ...schema,
    sections: schema.sections.map((section) => ({
      ...section,
      fields: section.fields
        .map(applyField)
        .filter((field): field is EFormFieldSchema => Boolean(field)),
    })),
  };
}

export const EFORM_META_LIST_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'id', label: 'ID' },
  { key: 'status', label: 'Status' },
  { key: 'approval', label: 'Approval' },
  { key: 'submitter', label: 'Submitted by' },
  { key: 'submittedAt', label: 'Date' },
];

export function defaultListColumns(schema: EFormSchema): EFormListColumnConfig[] {
  return [
    ...EFORM_META_LIST_COLUMNS.map((c) => ({ key: c.key, visible: true })),
    ...flattenSchemaFields(schema)
      .filter((f) => !isEFormDisplayField(f))
      .map((f) => ({ key: f.key, visible: true })),
  ];
}

/** Merge saved list-column prefs with current fields (keeps order; appends new fields). */
export function resolveListColumns(schema: EFormSchema): EFormListColumnConfig[] {
  const defaults = defaultListColumns(schema);
  const configured = schema.settings?.listColumns;
  if (!configured?.length) return defaults;
  const defaultKeys = new Set(defaults.map((d) => d.key));
  const seen = new Set<string>();
  const result: EFormListColumnConfig[] = [];
  for (const c of configured) {
    if (!defaultKeys.has(c.key) || seen.has(c.key)) continue;
    seen.add(c.key);
    result.push({ key: c.key, visible: c.visible !== false });
  }
  for (const d of defaults) {
    if (!seen.has(d.key)) result.push(d);
  }
  return result;
}

export function listColumnLabel(schema: EFormSchema, key: string): string {
  const meta = EFORM_META_LIST_COLUMNS.find((c) => c.key === key);
  if (meta) return meta.label;
  const field = flattenSchemaFields(schema).find((f) => f.key === key);
  return field?.label ?? key;
}

/** Select/radio fields that can be used as submissions-list filters. */
export function eligibleFilterFields(schema: EFormSchema): EFormFieldSchema[] {
  return flattenSchemaFields(schema).filter(
    (f) =>
      !isEFormDisplayField(f) &&
      (f.type === 'select' || f.type === 'radio') &&
      Boolean(f.key),
  );
}

/** Enabled filter field keys, in form (schema) field order. */
export function resolveFilterFields(schema: EFormSchema): string[] {
  const configured = schema.settings?.filterFields;
  if (!configured?.length) return [];
  const enabled = new Set(
    configured
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter((v) => v.length > 0),
  );
  return eligibleFilterFields(schema)
    .map((f) => f.key)
    .filter((key) => enabled.has(key));
}

/** Eligible filters for the builder UI: enabled first (form order), then the rest. */
export function orderedFilterFieldsForBuilder(schema: EFormSchema): EFormFieldSchema[] {
  const eligible = eligibleFilterFields(schema);
  const enabledKeys = new Set(resolveFilterFields(schema));
  return [
    ...eligible.filter((f) => enabledKeys.has(f.key)),
    ...eligible.filter((f) => !enabledKeys.has(f.key)),
  ];
}

export type EFormSchema = {
  sections: EFormSectionSchema[];
  /** Shown above the fill form. */
  instructions?: string;
  settings?: EFormSchemaSettings;
};

export function emptyEFormSchema(): EFormSchema {
  return {
    sections: [
      {
        id: 'section-1',
        heading: 'Section 1',
        columns: 2,
        fields: [],
      },
    ],
    instructions: '',
    settings: { requireApproval: false, listColumns: undefined },
  };
}

export function normalizeSectionColumns(value: unknown): EFormSectionColumns {
  const n = typeof value === 'number' ? value : Number(value);
  if ((EFORM_SECTION_COLUMNS as readonly number[]).includes(n)) return n as EFormSectionColumns;
  return 2;
}

/** Builder / fill: 12-unit row, stacked on small screens. */
export function sectionTwelveGridClass(gap = 'gap-2'): string {
  return `grid grid-cols-1 md:grid-cols-12 ${gap}`;
}

export function flattenSchemaFields(schema: EFormSchema): EFormFieldSchema[] {
  return (schema.sections ?? []).flatMap((s) => s.fields ?? []);
}

/** Printed layout blocks (heading, static text, memo, formatted text, spacer). */
export function isEFormDisplayField(field?: Pick<EFormFieldSchema, 'type' | 'display'> | null): boolean {
  if (!field) return false;
  return field.type === 'heading' || field.type === 'spacer' || field.display === true;
}

/** Spacer height in pixels (stored in defaultValue). */
export const EFORM_SPACER_PRESETS = [
  { value: 12, label: 'XS · 12px' },
  { value: 24, label: 'S · 24px' },
  { value: 40, label: 'M · 40px' },
  { value: 64, label: 'L · 64px' },
  { value: 96, label: 'XL · 96px' },
] as const;

export function eformSpacerHeight(field: Pick<EFormFieldSchema, 'defaultValue'>): number {
  const n = typeof field.defaultValue === 'number' ? field.defaultValue : Number(field.defaultValue);
  if (Number.isFinite(n) && n >= 4 && n <= 400) return Math.round(n);
  return 24;
}

function escapeDisplayHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Preserve typed Enter/returns when content is plain text rather than HTML. */
export function plainTextToDisplayHtml(text: string): string {
  const lines = String(text ?? '').replace(/\r\n/g, '\n').split('\n');
  return lines.map((line) => `<p>${escapeDisplayHtml(line) || '<br>'}</p>`).join('');
}

export function eformDisplayContent(field: EFormFieldSchema): string {
  if (field.type === 'richtext') {
    const html = typeof field.defaultValue === 'string' ? field.defaultValue : '';
    const raw = html.trim() ? html : field.label || '';
    if (!raw.trim()) return '';
    if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
    return plainTextToDisplayHtml(raw);
  }
  return field.label ?? '';
}

export function eformDisplayPreviewText(field: EFormFieldSchema): string {
  if (field.type === 'spacer') {
    return `Empty space · ${eformSpacerHeight(field)}px`;
  }
  if (field.type === 'richtext') {
    const raw = eformDisplayContent(field)
      .replace(/<\/p>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    return raw || field.label || 'Formatted text';
  }
  return eformDisplayContent(field) || field.label || 'Layout';
}

export function isEFormSchema(value: unknown): value is EFormSchema {
  return !!value && typeof value === 'object' && Array.isArray((value as EFormSchema).sections);
}

export function evaluateVisibleWhen(
  rule: EFormFieldVisibleWhen | undefined,
  values: Record<string, unknown>,
): boolean {
  if (!rule?.fieldKey) return true;
  const raw = values[rule.fieldKey];
  const str = raw == null ? '' : Array.isArray(raw) ? raw.map(String).join(',') : String(raw);
  switch (rule.operator) {
    case 'empty':
      return str === '' || (Array.isArray(raw) && raw.length === 0);
    case 'notEmpty':
      return str !== '' && !(Array.isArray(raw) && raw.length === 0);
    case 'contains':
      return str.toLowerCase().includes(String(rule.value ?? '').toLowerCase());
    case 'neq':
      return str !== String(rule.value ?? '');
    case 'eq':
    default:
      return str === String(rule.value ?? '');
  }
}

export function resolveDefaultValue(
  field: EFormFieldSchema,
  ctx?: { userDisplayName?: string },
): unknown {
  const v = field.defaultValue;
  if (v === undefined || v === null || v === '') return undefined;
  if (typeof v === 'string') {
    if (v === '__today__') return new Date().toISOString().slice(0, 10);
    if (v === '__userName__') return ctx?.userDisplayName ?? '';
  }
  return v;
}

function fieldTypeToInputType(type: EFormFieldType): string {
  switch (type) {
    case 'textarea':
      return 'textarea';
    case 'email':
      return 'email';
    case 'phone':
      return 'text';
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'time':
      return 'time';
    case 'select':
      return 'select';
    case 'radio':
      return 'radio';
    case 'checkbox':
      return 'checkbox';
    case 'switch':
      return 'switch';
    case 'checklist':
      return 'checkbox-multiselect';
    case 'richtext':
      return 'richtext';
    case 'heading':
      return 'text';
    case 'spacer':
    case 'signature':
    case 'file':
    case 'photo':
    case 'subform':
      return 'custom';
    case 'text':
    default:
      return 'text';
  }
}

export function compileFormSchema(schema: EFormSchema): {
  fields: FieldConfig[];
  formLayout: FormLayout;
} {
  const fields: FieldConfig[] = [];
  const layoutSections: FormLayout['sections'] = [];

  for (const section of schema.sections ?? []) {
    const columns = normalizeSectionColumns(section.columns);
    const fieldSpans: Record<string, string> = {};
    const fieldNames: string[] = [];

    for (const field of section.fields ?? []) {
      fieldNames.push(field.key);
      fieldSpans[field.key] = fieldColSpanClass(resolveFieldColSpan(field));

      const display = isEFormDisplayField(field);
      const config: FieldConfig = {
        name: field.key,
        label: field.label,
        type: display ? 'custom' : fieldTypeToInputType(field.type),
        required: Boolean(field.required) && !display,
        placeholder:
          field.placeholder ||
          (field.type === 'time' ? 'HH:MM' : field.type === 'phone' ? '+30 …' : undefined),
        helperText:
          field.helpText ||
          (field.type === 'time'
            ? 'Time (HH:MM)'
            : undefined),
        readOnly: Boolean(field.readOnly) || display || Boolean(field.calculatedFrom),
        disabled: display,
        validation: {
          required: Boolean(field.required) && !display,
          minLength: field.validation?.minLength,
          maxLength: field.validation?.maxLength,
          min: field.validation?.min,
          max: field.validation?.max,
          pattern: field.validation?.pattern
            ? (() => {
                try {
                  return new RegExp(field.validation!.pattern!);
                } catch {
                  return undefined;
                }
              })()
            : field.type === 'email'
              ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/
              : undefined,
          message: field.validation?.message,
        },
      };

      if (field.type === 'textarea') {
        config.rows = 4;
      }
      if (field.type === 'richtext') {
        config.rows = 6;
      }
      if (field.type === 'number') {
        config.min = field.validation?.min;
        config.max = field.validation?.max;
      }

      const useReference =
        (field.type === 'select' || field.type === 'radio') &&
        field.optionsSource === 'reference' &&
        Boolean(field.referenceKey);
      if (useReference) {
        const src = getEFormReferenceSource(field.referenceKey);
        if (src) {
          config.apiEndpoint = src.endpoint;
          config.valueField = src.valueField;
          config.labelField = src.labelField;
          if (!field.required) {
            config.options = [{ value: '', label: '—' }];
          }
        }
      } else if (field.type === 'checklist') {
        config.options = (field.options ?? []).map((o) => ({ value: o.value, label: o.label }));
      } else if (field.options?.length) {
        config.options = field.options.map((o) => ({ value: o.value, label: o.label }));
      }

      fields.push(config);
    }

    const visibilityDependsOn = section.visibleWhen?.fieldKey
      ? [section.visibleWhen.fieldKey]
      : undefined;

    layoutSections.push({
      id: section.id,
      title: section.heading,
      description: section.subHeading,
      fields: fieldNames,
      columns,
      gridCols: 'md:grid-cols-12',
      fieldSpans: Object.keys(fieldSpans).length ? fieldSpans : undefined,
      visibilityDependsOn,
      visibleWhen: section.visibleWhen
        ? (formData: Record<string, unknown>) => evaluateVisibleWhen(section.visibleWhen, formData)
        : undefined,
    });
  }

  return {
    fields,
    formLayout: { mode: 'default', sections: layoutSections },
  };
}

export function isEFormFileMeta(value: unknown): value is EFormFileMeta {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return typeof o.storageKey === 'string' && o.storageKey.length > 0 && typeof o.fileName === 'string';
}

export type SchemaValidationError = { fieldKey?: string; message: string };

export type SchemaPublishIssue = { fieldKey?: string; message: string };

/**
 * Client-side mirror of backend `validateSubmissionValues`.
 * Use `requireRequired: true` before submit; drafts may pass `false`.
 */
export function validateSubmissionValues(
  schema: EFormSchema,
  values: Record<string, unknown>,
  opts?: { requireRequired?: boolean },
): SchemaValidationError[] {
  const requireRequired = opts?.requireRequired !== false;
  const fields = flattenSchemaFields(schema);
  const known = new Set(fields.map((f) => f.key));
  const errors: SchemaValidationError[] = [];

  for (const key of Object.keys(values)) {
    if (!known.has(key)) {
      errors.push({ fieldKey: key, message: `Unknown field: ${key}` });
    }
  }

  for (const field of fields) {
    if (isEFormDisplayField(field)) continue;
    if (!evaluateVisibleWhen(field.visibleWhen, values)) continue;
    const section = schema.sections.find((s) => s.fields.some((f) => f.id === field.id));
    if (section && !evaluateVisibleWhen(section.visibleWhen, values)) continue;

    const raw = values[field.key];
    const empty =
      field.type === 'file'
        ? field.multiple
          ? !Array.isArray(raw) || raw.length === 0
          : !isEFormFileMeta(raw)
        : field.type === 'photo'
          ? !isEFormFileMeta(raw)
        : field.type === 'signature'
          ? !(
              isEFormFileMeta(raw) ||
              (typeof raw === 'string' && raw.trim().length > 0)
            )
          : field.type === 'subform'
            ? !Array.isArray(raw) || raw.length === 0
            : raw === undefined ||
              raw === null ||
              raw === '' ||
              (Array.isArray(raw) && raw.length === 0);

    if (requireRequired && field.required && !field.readOnly && empty) {
      errors.push({ fieldKey: field.key, message: `${field.label} is required` });
      continue;
    }
    if (empty) {
      if (field.type === 'subform' && field.minRows != null && field.minRows > 0 && requireRequired) {
        errors.push({
          fieldKey: field.key,
          message: `${field.label} requires at least ${field.minRows} row(s)`,
        });
      }
      continue;
    }

    const v = field.validation;
    if (
      field.type !== 'file' &&
      field.type !== 'photo' &&
      field.type !== 'subform' &&
      field.type !== 'signature'
    ) {
      if (v?.minLength != null && String(raw).length < v.minLength) {
        errors.push({
          fieldKey: field.key,
          message: v.message || `${field.label} must be at least ${v.minLength} characters`,
        });
      }
      if (v?.maxLength != null && String(raw).length > v.maxLength) {
        errors.push({
          fieldKey: field.key,
          message: v.message || `${field.label} must be at most ${v.maxLength} characters`,
        });
      }
    }

    switch (field.type) {
      case 'subform': {
        if (!Array.isArray(raw)) {
          errors.push({ fieldKey: field.key, message: `${field.label} must be a list of rows` });
          break;
        }
        if (field.minRows != null && raw.length < field.minRows) {
          errors.push({
            fieldKey: field.key,
            message: `${field.label} requires at least ${field.minRows} row(s)`,
          });
        }
        if (field.maxRows != null && raw.length > field.maxRows) {
          errors.push({
            fieldKey: field.key,
            message: `${field.label} allows at most ${field.maxRows} row(s)`,
          });
        }
        const nested = field.fields ?? [];
        raw.forEach((row, ri) => {
          if (!row || typeof row !== 'object' || Array.isArray(row)) {
            errors.push({
              fieldKey: field.key,
              message: `${field.label} row ${ri + 1} is invalid`,
            });
            return;
          }
          const rowValues = row as Record<string, unknown>;
          for (const nf of nested) {
            const cell = rowValues[nf.key];
            const cellEmpty =
              cell === undefined ||
              cell === null ||
              cell === '' ||
              (Array.isArray(cell) && cell.length === 0);
            if (requireRequired && nf.required && cellEmpty) {
              errors.push({
                fieldKey: field.key,
                message: `${field.label} row ${ri + 1}: ${nf.label} is required`,
              });
              continue;
            }
            if (cellEmpty) continue;
            if (nf.type === 'number') {
              const n = typeof cell === 'number' ? cell : Number(cell);
              if (Number.isNaN(n)) {
                errors.push({
                  fieldKey: field.key,
                  message: `${field.label} row ${ri + 1}: ${nf.label} must be a number`,
                });
              }
            }
            if (nf.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(cell))) {
              errors.push({
                fieldKey: field.key,
                message: `${field.label} row ${ri + 1}: ${nf.label} must be a valid email`,
              });
            }
          }
        });
        break;
      }
      case 'file': {
        if (field.multiple) {
          if (!Array.isArray(raw) || !raw.every(isEFormFileMeta)) {
            errors.push({ fieldKey: field.key, message: `${field.label} must be a list of files` });
          }
        } else if (!isEFormFileMeta(raw)) {
          errors.push({ fieldKey: field.key, message: `${field.label} must be a file` });
        }
        break;
      }
      case 'photo': {
        if (!isEFormFileMeta(raw)) {
          errors.push({ fieldKey: field.key, message: `${field.label} must be a photo` });
        }
        break;
      }
      case 'signature': {
        if (!isEFormFileMeta(raw) && typeof raw !== 'string') {
          errors.push({ fieldKey: field.key, message: `${field.label} must be a signature` });
        }
        break;
      }
      case 'number': {
        const n = typeof raw === 'number' ? raw : Number(raw);
        if (Number.isNaN(n)) {
          errors.push({ fieldKey: field.key, message: `${field.label} must be a number` });
        } else {
          if (v?.min != null && n < v.min) {
            errors.push({ fieldKey: field.key, message: `${field.label} must be ≥ ${v.min}` });
          }
          if (v?.max != null && n > v.max) {
            errors.push({ fieldKey: field.key, message: `${field.label} must be ≤ ${v.max}` });
          }
        }
        break;
      }
      case 'email': {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(raw))) {
          errors.push({ fieldKey: field.key, message: `${field.label} must be a valid email` });
        }
        break;
      }
      case 'checkbox':
      case 'switch':
        if (typeof raw !== 'boolean' && raw !== 'true' && raw !== 'false' && raw !== 0 && raw !== 1) {
          errors.push({ fieldKey: field.key, message: `${field.label} must be true/false` });
        }
        break;
      case 'checklist':
        if (!Array.isArray(raw)) {
          errors.push({ fieldKey: field.key, message: `${field.label} must be a list` });
        }
        break;
      default:
        if (v?.pattern) {
          try {
            if (!new RegExp(v.pattern).test(String(raw))) {
              errors.push({
                fieldKey: field.key,
                message: v.message || `${field.label} format is invalid`,
              });
            }
          } catch {
            /* ignore bad pattern */
          }
        }
        break;
    }
  }

  return errors;
}

/**
 * Extra checks before publish: unique keys/ids, calculatedFrom targets, no cycles.
 * Mirrors backend `validateSchemaForPublish`.
 */
export function validateSchemaForPublish(schema: EFormSchema): SchemaPublishIssue[] {
  const issues: SchemaPublishIssue[] = [];
  const fields = flattenSchemaFields(schema).filter((f) => !isEFormDisplayField(f));
  const byKey = new Map(fields.map((f) => [f.key, f]));

  const ids = fields.map((f) => f.id);
  const dupId = ids.find((id, i) => ids.indexOf(id) !== i);
  if (dupId) {
    issues.push({ message: `Duplicate field id: ${dupId}` });
  }

  for (const field of fields) {
    if (!field.calculatedFrom) continue;
    if (!byKey.has(field.calculatedFrom)) {
      issues.push({
        fieldKey: field.key,
        message: `${field.label}: calculatedFrom target "${field.calculatedFrom}" does not exist`,
      });
      continue;
    }
    const seen = new Set<string>();
    let cur: string | undefined = field.key;
    while (cur) {
      if (seen.has(cur)) {
        issues.push({
          fieldKey: field.key,
          message: `${field.label}: calculatedFrom cycle detected`,
        });
        break;
      }
      seen.add(cur);
      const sourceKey: string | undefined = byKey.get(cur)?.calculatedFrom;
      cur = sourceKey;
    }
  }

  for (const field of fields) {
    if (!field.visibleWhen?.fieldKey) continue;
    const anyKey = flattenSchemaFields(schema).some((f) => f.key === field.visibleWhen!.fieldKey);
    if (!anyKey) {
      issues.push({
        fieldKey: field.key,
        message: `${field.label}: visibleWhen references unknown field "${field.visibleWhen.fieldKey}"`,
      });
    }
  }

  return issues;
}

/** Drop values for fields currently hidden by visibility rules (field or section).
 * Also strips UI-only subform `_rowId` keys so they are not persisted/exported.
 */
export function stripHiddenFieldValues(
  schema: EFormSchema,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...values };
  for (const section of schema.sections ?? []) {
    const sectionVisible = evaluateVisibleWhen(section.visibleWhen, values);
    for (const field of section.fields ?? []) {
      if (isEFormDisplayField(field)) {
        delete next[field.key];
        continue;
      }
      const visible = sectionVisible && evaluateVisibleWhen(field.visibleWhen, values);
      if (!visible) {
        delete next[field.key];
        continue;
      }
      if (field.type === 'subform' && Array.isArray(next[field.key])) {
        next[field.key] = (next[field.key] as unknown[]).map((row) => {
          if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
          const { [EFORM_SUBFORM_ROW_ID_KEY]: _omit, ...rest } = row as Record<string, unknown>;
          return rest;
        });
      }
    }
  }
  return next;
}

/** Map validation errors to fieldKey → first message (for form UI). */
export function validationErrorsByField(
  errors: SchemaValidationError[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const e of errors) {
    if (!e.fieldKey || map[e.fieldKey]) continue;
    map[e.fieldKey] = e.message;
  }
  return map;
}

export function formatFieldDisplayValue(
  field: EFormFieldSchema,
  value: unknown,
  opts?: { referenceLabels?: Map<string, string> },
): string {
  if (isEFormDisplayField(field)) return '';
  // Recover file payloads if schema type is missing/outdated
  if (value === null || value === undefined || value === '') return '—';
  switch (field.type) {
    case 'checkbox':
    case 'switch':
      return value === true || value === 'true' || value === 1 ? 'Yes' : 'No';
    case 'date': {
      const raw = String(value).trim();
      if (!raw) return '—';
      // Date-only (YYYY-MM-DD) — parse as local calendar date to avoid UTC day shift.
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [y, m, d] = raw.split('-').map(Number);
        return formatDate(new Date(y, m - 1, d)) || raw;
      }
      return formatDate(raw) || raw;
    }
    case 'time': {
      const raw = String(value).trim();
      return raw || '—';
    }
    case 'checklist':
      if (Array.isArray(value)) {
        const labels = value.map((v) => {
          const key = String(v);
          const opt = field.options?.find((o) => String(o.value) === key);
          return opt?.label ?? opts?.referenceLabels?.get(key) ?? key;
        });
        return labels.join(', ') || '—';
      }
      return String(value);
    case 'file':
    case 'photo': {
      if (Array.isArray(value)) {
        const names = value.filter(isEFormFileMeta).map((f) => f.fileName);
        return names.join(', ') || '—';
      }
      return isEFormFileMeta(value) ? value.fileName : '—';
    }
    case 'subform': {
      if (!Array.isArray(value)) return '—';
      const n = value.length;
      if (n === 0) return '0 rows';
      return n === 1 ? '1 row' : `${n} rows`;
    }
    case 'select':
    case 'radio': {
      const key = String(value);
      const opt = field.options?.find((o) => String(o.value) === key);
      if (opt?.label) return opt.label;
      const fromRef = opts?.referenceLabels?.get(key);
      if (fromRef) return fromRef;
      return key;
    }
    case 'richtext':
    case 'textarea': {
      const s = String(value).replace(/<[^>]+>/g, ' ').trim();
      return s.length > 80 ? `${s.slice(0, 80)}…` : s || '—';
    }
    case 'signature': {
      if (value && typeof value === 'object' && 'storageKey' in (value as object)) {
        const stamp =
          (value as { stamp?: unknown }).stamp &&
          typeof (value as { stamp?: unknown }).stamp === 'object'
            ? ' + stamp'
            : '';
        return `Signed${stamp}`;
      }
      if (typeof value === 'string' && value.trim()) return 'Typed name';
      return '—';
    }
    default: {
      // Recover file payloads if schema type is missing/outdated
      if (isEFormFileMeta(value)) return value.fileName;
      if (Array.isArray(value) && value.some(isEFormFileMeta)) {
        return value.filter(isEFormFileMeta).map((f) => f.fileName).join(', ') || '—';
      }
      if (Array.isArray(value) && value.every((r) => r && typeof r === 'object')) {
        const n = value.length;
        return n === 0 ? '0 rows' : n === 1 ? '1 row' : `${n} rows`;
      }
      if (typeof value === 'object') return '—';
      const raw = String(value);
      // ISO datetime strings in generic columns
      if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
        return formatDateTime(raw) || raw;
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [y, m, d] = raw.split('-').map(Number);
        return formatDate(new Date(y, m - 1, d)) || raw;
      }
      return raw;
    }
  }
}

/** Normalize a submission value into file metadata rows (supports single or multi). */
export function coerceEFormFiles(value: unknown): EFormFileMeta[] {
  if (Array.isArray(value)) return value.filter(isEFormFileMeta);
  return isEFormFileMeta(value) ? [value] : [];
}

export function newFieldId(): string {
  return `f_${Math.random().toString(36).slice(2, 10)}`;
}

export function newSectionId(): string {
  return `s_${Math.random().toString(36).slice(2, 10)}`;
}

export function createPaletteField(
  type: EFormFieldType,
  opts?: { display?: boolean; label?: string },
): EFormFieldSchema {
  const id = newFieldId();
  const display = type === 'heading' || type === 'spacer' || Boolean(opts?.display);
  const labels: Record<EFormFieldType, string> = {
    text: 'Text Field',
    textarea: 'Notes',
    email: 'Email',
    phone: 'Phone',
    number: 'Number',
    date: 'Date',
    time: 'Time',
    select: 'Dropdown',
    radio: 'Radio',
    checkbox: 'Checkbox',
    switch: 'Toggle',
    checklist: 'Checklist',
    richtext: 'Rich text',
    signature: 'Signature',
    heading: 'Heading',
    spacer: 'Empty space',
    file: 'Files',
    photo: 'Photo',
    subform: 'Sub-form',
  };
  const field: EFormFieldSchema = {
    id,
    key: `${type}_${id.slice(2)}`,
    type,
    label: opts?.label || labels[type],
    required: false,
    placeholder: '',
    colSpan: fieldUsesFullWidthDefault(type, display) ? 12 : 6,
  };
  if (type === 'spacer') {
    field.defaultValue = 24;
  }
  if (type === 'file') {
    field.multiple = true;
    field.accept = '';
  }
  if (type === 'photo') {
    field.multiple = false;
    field.accept =
      'image/png,image/jpeg,image/gif,image/webp,image/bmp,.png,.jpg,.jpeg,.gif,.webp,.bmp';
  }
  if (type === 'subform') {
    const col1 = createPaletteField('text');
    col1.label = 'Item';
    col1.key = `item_${col1.id.slice(2)}`;
    const col2 = createPaletteField('number');
    col2.label = 'Qty';
    col2.key = `qty_${col2.id.slice(2)}`;
    field.fields = [col1, col2];
    field.minRows = 0;
    field.maxRows = 50;
    field.rowEditor = 'modal';
  }
  if (type === 'select' || type === 'radio' || type === 'checklist') {
    field.optionsSource = 'static';
    field.options = [
      { value: 'option_1', label: 'Option 1' },
      { value: 'option_2', label: 'Option 2' },
    ];
  }
  if (display) {
    field.readOnly = true;
    field.display = true;
    if (type === 'richtext') field.defaultValue = '';
  }
  return field;
}

export function createSubformNestedField(type: EFormSubformNestedType): EFormFieldSchema {
  const field = createPaletteField(type);
  field.colSpan = 6;
  delete field.fields;
  delete field.minRows;
  delete field.maxRows;
  delete field.rowEditor;
  delete field.multiple;
  delete field.accept;
  delete field.display;
  return field;
}

/** Stable identity for sub-form rows (React keys); stripped from nested field validation. */
export const EFORM_SUBFORM_ROW_ID_KEY = '_rowId';

export function newSubformRowId(): string {
  return `r_${Math.random().toString(36).slice(2, 10)}`;
}

export function emptySubformRow(subform: EFormFieldSchema): Record<string, unknown> {
  const row: Record<string, unknown> = { [EFORM_SUBFORM_ROW_ID_KEY]: newSubformRowId() };
  for (const f of subform.fields ?? []) {
    if (f.type === 'checkbox' || f.type === 'switch') row[f.key] = false;
    else if (f.type === 'number') row[f.key] = '';
    else row[f.key] = '';
  }
  return row;
}

export function coerceSubformRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter((r) => r && typeof r === 'object' && !Array.isArray(r)) as Record<
    string,
    unknown
  >[];
}

/** Ensure every row has a stable `_rowId` (mutates via returned array; caller should persist). */
export function ensureSubformRowIds(
  rows: Record<string, unknown>[],
): { rows: Record<string, unknown>[]; changed: boolean } {
  let changed = false;
  const next = rows.map((r) => {
    const id = r[EFORM_SUBFORM_ROW_ID_KEY];
    if (typeof id === 'string' && id) return r;
    changed = true;
    return { ...r, [EFORM_SUBFORM_ROW_ID_KEY]: newSubformRowId() };
  });
  return { rows: next, changed };
}

export function subformRowKey(row: Record<string, unknown>, fallbackIndex: number): string {
  const id = row[EFORM_SUBFORM_ROW_ID_KEY];
  return typeof id === 'string' && id ? id : `idx_${fallbackIndex}`;
}

export function duplicateField(field: EFormFieldSchema): EFormFieldSchema {
  const id = newFieldId();
  return {
    ...JSON.parse(JSON.stringify(field)),
    id,
    key: `${field.type}_${id.slice(2)}`,
    label: `${field.label} (copy)`,
  };
}

export function duplicateSection(section: EFormSectionSchema): EFormSectionSchema {
  const id = newSectionId();
  return {
    ...JSON.parse(JSON.stringify(section)),
    id,
    heading: `${section.heading || 'Section'} (copy)`,
    fields: (section.fields ?? []).map((f) => duplicateField(f)),
  };
}

export type EFormPaletteItem = {
  id: string;
  type: EFormFieldType;
  label: string;
  category: 'layout' | 'text' | 'date' | 'other';
  display?: boolean;
};

export const FIELD_PALETTE: EFormPaletteItem[] = [
  { id: 'heading', type: 'heading', label: 'Heading', category: 'layout', display: true },
  { id: 'spacer', type: 'spacer', label: 'Empty space', category: 'layout' },
  { id: 'display_text', type: 'text', label: 'Static text', category: 'layout', display: true },
  { id: 'display_memo', type: 'textarea', label: 'Memo', category: 'layout', display: true },
  { id: 'display_richtext', type: 'richtext', label: 'Formatted text', category: 'layout', display: true },
  { id: 'text', type: 'text', label: 'Text Field', category: 'text' },
  { id: 'textarea', type: 'textarea', label: 'Notes', category: 'text' },
  { id: 'email', type: 'email', label: 'Email', category: 'text' },
  { id: 'phone', type: 'phone', label: 'Phone', category: 'text' },
  { id: 'richtext', type: 'richtext', label: 'Rich text', category: 'text' },
  { id: 'number', type: 'number', label: 'Number', category: 'text' },
  { id: 'date', type: 'date', label: 'Date', category: 'date' },
  { id: 'time', type: 'time', label: 'Time', category: 'date' },
  { id: 'select', type: 'select', label: 'Dropdown', category: 'other' },
  { id: 'radio', type: 'radio', label: 'Radio', category: 'other' },
  { id: 'checkbox', type: 'checkbox', label: 'Checkbox', category: 'other' },
  { id: 'switch', type: 'switch', label: 'Toggle', category: 'other' },
  { id: 'checklist', type: 'checklist', label: 'Checklist', category: 'other' },
  { id: 'signature', type: 'signature', label: 'Signature', category: 'other' },
  { id: 'photo', type: 'photo', label: 'Photo', category: 'other' },
  { id: 'file', type: 'file', label: 'Files', category: 'other' },
  { id: 'subform', type: 'subform', label: 'Sub-form', category: 'other' },
];
